/**
 * Electron Adapter - Desktop environment platform implementation
 * Wraps window.electronAPI calls for type safety and abstraction
 */

import type { PlatformAPI, UploadResult } from './types';

/**
 * Type-safe wrapper for IPC channel names
 */
const CHANNELS = {
  SELECT_FOLDER: 'folder:select',
  GET_IMAGES: 'images:get',
  READ_EXIF: 'exif:read',
  READ_BATCH_EXIF: 'exif:read-batch',
  WRITE_EXIF: 'exif:write',
  APPLY_CHANGES: 'exif:apply',
  GENERATE_LLM_EXIF: 'llm:generate-exif',
  SAVE_LLM_CONFIG: 'llm:save-config',
  GET_LLM_CONFIG: 'llm:get-config',
  GET_THUMBNAIL: 'thumbnail:get',
} as const;

/**
 * Check if we're running in Electron environment
 */
function isElectronEnvironment(): boolean {
  // Use compile-time global set by Vite define config
  // vite.config.ts sets __IS_ELECTRON__ = 'true'
  // vite.web.config.ts sets __IS_ELECTRON__ = 'false'
  return __IS_ELECTRON__;
}

/**
 * Assert that electronAPI is available
 */
function getElectronAPI(): NonNullable<Window['electronAPI']> {
  if (!isElectronEnvironment()) {
    throw new Error('Electron API not available. This adapter requires Electron environment.');
  }
  return window.electronAPI!;
}

export const electronAdapter: PlatformAPI = {
  isElectron: true,

  invoke: async (channel: string, ...args: unknown[]): Promise<unknown> => {
    return getElectronAPI().invoke(channel, ...args);
  },

  on: (channel: string, callback: (...args: unknown[]) => void): (() => void) | void => {
    return getElectronAPI().on(channel, callback);
  },

  off: (channel: string, callback: (...args: unknown[]) => void): void => {
    return getElectronAPI().off(channel, callback);
  },

  selectFolder: async (): Promise<string | null> => {
    return getElectronAPI().invoke(CHANNELS.SELECT_FOLDER);
  },

  uploadFiles: async (): Promise<UploadResult | null> => {
    // Not used in Electron - files are read directly from filesystem
    return null;
  },

  getInstanceId: (): string | null => {
    // Not used in Electron - different session management
    return null;
  },

  getImages: async (folderPath: string): Promise<Array<{ name: string; path: string; size: number; modified: string }>> => {
    return getElectronAPI().invoke(CHANNELS.GET_IMAGES, folderPath) || [];
  },

  readExif: async (path: string): Promise<Record<string, unknown>> => {
    return getElectronAPI().invoke(CHANNELS.READ_EXIF, path) || {};
  },

  readBatchExif: async (paths: string[]): Promise<Record<string, Record<string, unknown>>> => {
    return getElectronAPI().invoke(CHANNELS.READ_BATCH_EXIF, paths) || {};
  },

  applyChanges: async (changes: Array<{ path: string; tags: Record<string, unknown> }>): Promise<Array<{ path: string; success: boolean; error?: string }>> => {
    return getElectronAPI().invoke(CHANNELS.APPLY_CHANGES, changes) || [];
  },

  generateLLMExif: async (config: Record<string, unknown>, imagePath: string, promptTemplate?: string): Promise<unknown> => {
    return getElectronAPI().invoke(
      CHANNELS.GENERATE_LLM_EXIF,
      config,
      imagePath,
      promptTemplate
    );
  },

  saveLLMConfig: async (config: Record<string, unknown>): Promise<boolean> => {
    try {
      await getElectronAPI().invoke(CHANNELS.SAVE_LLM_CONFIG, config);
      return true;
    } catch {
      return false;
    }
  },

  getLLMConfig: async (): Promise<Record<string, unknown> | null> => {
    return getElectronAPI().invoke(CHANNELS.GET_LLM_CONFIG);
  },

  getThumbnail: async (path: string): Promise<string> => {
    return getElectronAPI().invoke(CHANNELS.GET_THUMBNAIL, path) || '';
  },

  showNotification: (type: 'info' | 'success' | 'warning' | 'error', message: string): void => {
    // In Electron, notifications come through IPC from main process
    // This is a no-op on renderer side - notifications are sent from main
    console.log(`[Notification - ${type}]: ${message}`);
  },

  logMessage: (message: string): void => {
    // In Electron, logs come through IPC 'log' channel
    // This is a no-op here - log subscription happens via 'on' method
    console.log(`[Log]: ${message}`);
  },
};

export { isElectronEnvironment };
export default electronAdapter;
